import { useRef } from "react";

/** Keep an ambiguous HTTP retry on the same intent, but never reuse an old
 * UUID after editing the draft (including editing it back to its old value). */
export function useBroadcastRequestId(intent: string) {
  const request = useRef<{ intent: string; id: string } | null>(null);
  if (!request.current || request.current.intent !== intent) {
    request.current = { intent, id: crypto.randomUUID() };
  }
  return request.current.id;
}

/** Bound HTTP AND response-body parsing, even if the transport ignores abort. */
export async function boundedBroadcastRequest<T>(
  controller: AbortController,
  work: () => Promise<T>,
  timeoutMs = 20_000,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error("Request timed out. Delivery is unknown; check Recent broadcasts before trying again."));
          controller.abort();
        }, timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}
